import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format as dateFnsFormat,
  isValid,
  parseISO,
} from "date-fns";
import type { Locale } from "date-fns";
import { enUS, pl } from "date-fns/locale";
import i18next from "i18next";

type DateInput = Date | string | number;

export const DISPLAY_DATE_FORMAT = "dd.MM.yyyy";
export const DISPLAY_DATE_TIME_FORMAT = "dd.MM.yyyy, HH:mm";

export const dateFnsLocale = (): Locale =>
  i18next.language?.startsWith("pl") ? pl : enUS;

export const toDate = (input: DateInput): Date => {
  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input);
  return parseISO(input.includes(" ") ? input.replace(" ", "T") : input);
};

export const parseUtcDate = (input: string): Date => {
  const iso = input.includes(" ") ? input.replace(" ", "T") : input;
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
};

export const formatUtcDate = (
  input: string | null | undefined,
  pattern: string,
): string => {
  if (input == null || input === "") return "";
  const date = parseUtcDate(input);
  return isValid(date)
    ? dateFnsFormat(date, pattern, { locale: dateFnsLocale() })
    : "";
};

export const toUtcDateTimeString = (date: Date): string =>
  date.toISOString().slice(0, 19).replace("T", " ");

export const formatDate = (
  input: DateInput | null | undefined,
  pattern: string,
): string => {
  if (input == null || input === "") return "";
  const date = toDate(input);
  return isValid(date)
    ? dateFnsFormat(date, pattern, { locale: dateFnsLocale() })
    : "";
};

export const formatDisplayDate = (input: DateInput | null | undefined) =>
  formatDate(input, DISPLAY_DATE_FORMAT);

export const formatDisplayDateTime = (input: DateInput | null | undefined) =>
  formatDate(input, DISPLAY_DATE_TIME_FORMAT);

export const daysFromToday = (input: DateInput, today: Date = new Date()) =>
  differenceInCalendarDays(toDate(input), today);

export interface RelativeTime {
  unit: "now" | "minutes" | "hours" | "days";
  count: number;
}

export const relativeToNow = (input: DateInput, now: Date): RelativeTime => {
  const date = toDate(input);
  const minutes = Math.max(0, differenceInMinutes(now, date));
  if (minutes < 1) return { unit: "now", count: 0 };
  if (minutes < 60) return { unit: "minutes", count: minutes };

  const hours = differenceInHours(now, date);
  if (hours < 24) return { unit: "hours", count: hours };

  return {
    unit: "days",
    count: Math.max(1, differenceInCalendarDays(now, date)),
  };
};

export interface RelativeDayLabels {
  today: string;
  tomorrow: string;
}

export const formatRelativeDay = (
  input: DateInput,
  today: Date,
  {
    labels = {
      today: i18next.t("relativeDay.today"),
      tomorrow: i18next.t("relativeDay.tomorrow"),
    },
    locale = dateFnsLocale(),
  }: { labels?: RelativeDayLabels; locale?: Locale } = {},
): string => {
  const date = toDate(input);
  const days = differenceInCalendarDays(date, today);
  if (days === 0) return labels.today;
  if (days === 1) return labels.tomorrow;
  const weekday = dateFnsFormat(date, "EEEE", { locale });
  return weekday.charAt(0).toLocaleUpperCase(locale.code) + weekday.slice(1);
};

export function toApiDate(
  input?: DateInput | null,
  pattern = "yyyy-MM-dd",
): string | null {
  if (!input) return null;
  const date = toDate(input);
  return isValid(date) ? dateFnsFormat(date, pattern) : null;
}
